import { first } from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";
import ApiClient from "../../../../../lib/apiClient";
import GridViewApiClient from "./apiClients/gridViewApiClient";
import GridViewUpdateFilterSettingsDTO from "./types/GridViewUpdateFilterSettingsDTO";
import GridViewReadDTO from "./types/GridViewReadDTO";
import GridViewCreateDTO from "./types/GridViewCreateDTO";
import GridView from "./types/GridView";
import GridColumn from "../../../GridColumns/GridColumn";
import { GridFilterSettings } from "../../types/GridFilterSettings";
import { GridBaseSettings } from "../../types/GridBaseSettings";
import AppToasterV2 from "../../../../ToastV2/ToastV2";

export interface GridViewModule {
  currentGridView: GridView | undefined;
  currentGridViewKey: string;
  userGridViews: GridViewReadDTO[] | undefined;
  gridBaseSettings: GridBaseSettings | undefined;
  getGridView: (gridViewKey: string) => GridView;
  updateViewColumnSettings: (
    gridViewKey: string,
    columnSettings: string[]
  ) => Promise<void>;
  updateViewMetricSettings: (
    gridViewKey: string,
    metricSettings: string[]
  ) => Promise<void>;
  setDefaultView: (gridViewKey: string) => Promise<void>;
  baseGridColumnsConfiguration: GridColumn[] | undefined;
  assignCurrentGridView: (
    gridViewKey: string,
    gridColumnsConfiguration: GridColumn[] | undefined,
    gridViews: GridViewReadDTO[]
  ) => void;

  loadViews: (
    gridTitle: string,
    gridId: string,
    columnsConfiguration: GridColumn[],
    getColumnsConfigurationForUser: () => GridColumn[],
    shouldCreateAutoView?: boolean,
    defaultGridViewTitle?: string,
    gridAutoName?: string
  ) => Promise<void>;

  updateViewFilterSettings: (
    gridId: string,
    gridFilterSettings: GridFilterSettings
  ) => Promise<void>;
}

export function useGridViewModule(
  apiClient: ApiClient,
  pathToUserViewApi?: string,
  refreshViewsAfterSaving: boolean = false
): GridViewModule {
  const [currentGridView, setCurrentGridView] = useState<GridView>();
  const [currentGridViewKey, setCurrentGridViewKey] = useState<string>("-");
  const [userGridViews, setUserGridViews] = useState<GridViewReadDTO[]>();
  const [baseGridColumnsConfiguration, setBaseGridColumnsConfiguration] =
    useState<GridColumn[]>();
  const [gridBaseSettings, setGridBaseSettings] = useState<GridBaseSettings>();
  const gridViewApiClient = useRef<GridViewApiClient>(
    new GridViewApiClient(apiClient, pathToUserViewApi)
  );

  const createView = useCallback(
    async (
      gridId: string,
      gridViewCreateDTO: GridViewCreateDTO
    ): Promise<GridViewReadDTO> => {
      const newGridView = await gridViewApiClient.current.createView(
        gridId,
        gridViewCreateDTO
      );
      return newGridView;
    },
    []
  );

  const createAutoView = useCallback(
    async (
      gridId: string,
      gridAutoName: string,
      getColumnsConfigurationForUser: () => GridColumn[]
    ): Promise<GridViewReadDTO> => {
      // Get config by role
      const columnsConfiguration = getColumnsConfigurationForUser();

      // Create
      const newGridView = await createView(gridId, {
        title: gridAutoName,
        columnSettings: [...columnsConfiguration].map((x) => x.field).join(","),
        isDefault: true,
      } as GridViewCreateDTO);

      return newGridView;
    },
    [createView]
  );

  const assignCurrentGridView = useCallback(
    (
      id: string,
      gridColumnsConfiguration: GridColumn[] | undefined,
      gridViews: GridViewReadDTO[]
    ) => {
      const gridViewDto = gridViews.find((x) => x.id === id);
      if (gridColumnsConfiguration && gridViewDto) {
        const gridView = new GridView(gridViewDto, gridColumnsConfiguration);
        setCurrentGridView(gridView);
      }
    },
    [setCurrentGridView]
  );

  const loadViews = useCallback(
    async (
      gridTitle: string,
      gridId: string,
      columnsConfiguration: GridColumn[],
      getColumnsConfigurationForUser: () => GridColumn[],
      shouldCreateAutoView?: boolean,
      defaultGridViewTitle?: string,
      gridAutoName?: string
    ) => {
      // Fetch
      let gridViewDTOs = await gridViewApiClient.current.fetchViews(gridId);

      // If None, Auto Create - Note that if the application automatically creates system views on get, this will not be called
      if (shouldCreateAutoView && gridViewDTOs.length <= 0) {
        gridViewDTOs = [
          await createAutoView(
            gridId,
            gridAutoName ?? "Auto",
            getColumnsConfigurationForUser
          ),
        ];
      }

      // Setup observables
      setGridBaseSettings({ id: gridId, title: gridTitle });
      setBaseGridColumnsConfiguration(columnsConfiguration);
      setUserGridViews(gridViewDTOs);

      // Determine the default grid view
      let current = undefined;

      // Primary: user default view from API
      if (!current) {
        current = gridViewDTOs.find((x) => x.isDefault === true);
      }

      // Secondary: defaultGridViewTitle property (if set)
      if (defaultGridViewTitle) {
        current = gridViewDTOs.find((x) => x.title === defaultGridViewTitle);
      }

      // Fallback: first view in the list
      if (!current) {
        current = first(gridViewDTOs);
      }

      if (current) {
        assignCurrentGridView(current.id, columnsConfiguration, gridViewDTOs);
      }
    },
    [
      assignCurrentGridView,
      createAutoView,
      setGridBaseSettings,
      setBaseGridColumnsConfiguration,
      setUserGridViews,
    ]
  );

  const updateViewFilterSettings = useCallback(
    async (
      gridId: string,
      gridFilterSettings: GridFilterSettings
    ): Promise<void> => {
      if (!userGridViews || !currentGridView) {
        return;
      }

      // Setup
      const columnSettingFields = gridFilterSettings.rawColumnSettings.map(
        (x) => x.field
      );

      const updateFilterSettingsDTO = {
        columnSettings: columnSettingFields.join(","),
        filterSettings: JSON.stringify(gridFilterSettings.rawFilterSettings),
        sortSettings: JSON.stringify(gridFilterSettings.rawSortSettings),
      } as GridViewUpdateFilterSettingsDTO;

      // Update Local
      const idx = userGridViews.findIndex((x) => x.id === currentGridView?.id);
      if (idx < 0) {
        return;
      }

      userGridViews[idx].columnSettings = columnSettingFields;
      userGridViews[idx].filterSettings =
        updateFilterSettingsDTO.filterSettings;
      userGridViews[idx].sortSettings = updateFilterSettingsDTO.sortSettings;

      // Update
      await gridViewApiClient.current.updateViewFilterSettings(
        gridId,
        currentGridView.id,
        updateFilterSettingsDTO
      );

      // Refresh current grid view in-memory so updated column order is used by any new grid instances
      assignCurrentGridView(
        currentGridView.id,
        baseGridColumnsConfiguration,
        userGridViews
      );

      if (refreshViewsAfterSaving) {
        const gridViewDTOs = await gridViewApiClient.current.fetchViews(gridId);

        for (const gridViewDTO of gridViewDTOs) {
          const existingGridView = userGridViews.find(
            (x) => x.title === gridViewDTO.title
          );
          if (existingGridView) {
            existingGridView.columnSettings = gridViewDTO.columnSettings;
            existingGridView.filterSettings = gridViewDTO.filterSettings;
            existingGridView.sortSettings = gridViewDTO.sortSettings;
          }
        }
      }
    },
    [currentGridView?.id, userGridViews, refreshViewsAfterSaving]
  );

  const updateViewColumnSettings = useCallback(
    async (viewId: string, columnSettings: string[]): Promise<void> => {
      if (!userGridViews || !currentGridView) {
        return;
      }

      // Update Local
      const idx = userGridViews.findIndex((x) => x.id === currentGridView?.id);
      if (idx < 0) {
        return;
      }

      userGridViews[idx].columnSettings = columnSettings;

      // Update
      await gridViewApiClient.current.updateViewColumnSettings(
        userGridViews[idx].gridId,
        viewId,
        columnSettings.join(",")
      );

      if (currentGridView.id === viewId) {
        assignCurrentGridView(
          viewId,
          baseGridColumnsConfiguration,
          userGridViews
        );
      }

      if (refreshViewsAfterSaving) {
        const gridViewDTOs = await gridViewApiClient.current.fetchViews(
          currentGridView.id
        );

        for (const gridViewDTO of gridViewDTOs) {
          const existingGridView = userGridViews.find(
            (x) => x.title === gridViewDTO.title
          );
          if (existingGridView) {
            existingGridView.columnSettings = gridViewDTO.columnSettings;
            existingGridView.filterSettings = gridViewDTO.filterSettings;
            existingGridView.sortSettings = gridViewDTO.sortSettings;
          }
        }
      }
    },
    [
      currentGridView,
      userGridViews,
      assignCurrentGridView,
      refreshViewsAfterSaving,
    ]
  );

  const updateViewMetricSettings = useCallback(
    async (viewId: string, metricSettings: string[]): Promise<void> => {
      if (!userGridViews || !currentGridView) {
        return;
      }

      // Update Local
      const idx = userGridViews.findIndex((x) => x.id === currentGridView?.id);
      if (idx < 0) {
        return;
      }

      userGridViews[idx].metricSettings = metricSettings.join(",");

      // Update
      await gridViewApiClient.current.updateViewMetricsSettings(
        userGridViews[idx].gridId,
        viewId,
        metricSettings.join(",")
      );

      if (currentGridView.id === viewId) {
        assignCurrentGridView(
          viewId,
          baseGridColumnsConfiguration,
          userGridViews
        );
      }
    },
    [currentGridView, userGridViews, assignCurrentGridView]
  );

  const setDefaultView = useCallback(
    async (id: string) => {
      if (!userGridViews) {
        return;
      }

      // Update Local
      const idx = userGridViews.findIndex((x) => x.id === id);
      if (idx < 0) {
        return;
      }

      const updatedGridViews = await gridViewApiClient.current.setDefaultView(
        userGridViews[idx].gridId,
        id
      );
      if (updatedGridViews) {
        setUserGridViews(updatedGridViews);
        AppToasterV2.showSuccessAppToaster(
          `Default view set to ${userGridViews[idx].title}.`
        );
      }
    },
    [userGridViews, setUserGridViews]
  );

  const getGridView = useCallback(
    (id: string): GridView => {
      if (!userGridViews || !baseGridColumnsConfiguration) {
        throw `No views data loaded.`;
      }

      const gridViewDto = userGridViews?.find((x) => x.id === id);

      if (!gridViewDto) {
        throw `Grid View ${id} could not be found.`;
      }

      const gridView = new GridView(gridViewDto, baseGridColumnsConfiguration);

      return gridView!;
    },
    [userGridViews, baseGridColumnsConfiguration]
  );

  useEffect(() => {
    setCurrentGridViewKey(
      `${currentGridView?.id ?? "-"}-${
        currentGridView?.columnSettings.join("") ?? "-"
      }`
    );
  }, [currentGridView, setCurrentGridViewKey]);

  return {
    currentGridView,
    currentGridViewKey,
    userGridViews,
    loadViews,
    updateViewFilterSettings,
    gridBaseSettings,
    getGridView,
    updateViewColumnSettings,
    setDefaultView,
    baseGridColumnsConfiguration,
    assignCurrentGridView,
    updateViewMetricSettings,
  };
}
